<template>
  <div class="leapo-ai">
    <!-- 模块1：Banner -->
    <section class="la-banner">
      <div class="la-banner__bg">
        <img src="/banner-leapoAI背景图.jpg" alt="" class="la-banner__bg-img" />
      </div>
      <div class="la-banner__overlay" />
      <div class="la-container la-banner__inner">
        <div class="la-banner__grid">
          <AnimatedSection>
            <h1 class="la-banner__title">LeapoAI</h1>
            <h2 class="la-banner__subtitle">让企业的每个岗位都有AI同事</h2>
            <p class="la-banner__desc">
              面向企业的AI智能体平台，快速对接ERP、CRM、OA等内部系统，将企业数字化系统进行AI升级。AI数字员工覆盖销售市场、财务行政、人力法务、供应链运营等全职能，7×24小时在线协同，数据分析辅助经营决策，让组织更智能、更高效。
            </p>
            <a href="https://leapoai.qcos.cn" target="_blank" rel="noopener noreferrer" class="la-btn la-btn--primary la-btn--arrow">
              <span>立即体验</span>
              <LucideIcon name="arrow-right" :size="16" class="la-btn__icon" />
            </a>
          </AnimatedSection>
          <AnimatedSection :delay="200">
            <div class="la-banner__image-wrap">
              <img src="/banner-leapoAI右边图.png" alt="LeapoAI 配图" class="la-banner__image" />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    <!-- 模块2：三大挑战 -->
    <section class="la-section la-section--gray">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">企业AI升级的三大挑战</h2>
            <p class="la-section__desc">
              数字化基础已建成，如何向AI升级、让数据真正产生价值，是企业面临的新课题
            </p>
          </div>
        </AnimatedSection>
        <div class="la-grid la-grid--3">
          <AnimatedSection v-for="(item, i) in challenges" :key="i" :delay="i * 100">
            <div class="la-card la-card--challenge">
              <div class="la-card__header">
                <div class="la-card__icon-wrap la-card__icon-wrap--blue-light">
                  <LucideIcon :name="challengeIconNames[i]" :size="20" class="la-card__icon la-icon--blue" />
                </div>
                <h3 class="la-card__title">{{ item.title }}</h3>
              </div>
              <p class="la-card__desc">{{ item.desc }}</p>
              <div class="la-tag-list">
                <span v-for="(tag, j) in item.tags" :key="j" class="la-tag">{{ tag }}</span>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    <!-- 模块3：AI数字员工矩阵 -->
    <section class="la-section">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">AI数字员工矩阵</h2>
            <p class="la-section__desc">
              覆盖销售、财务、人力、法务、供应链、产品、设计等7大职能领域，每个岗位都有7×24小时待命的AI同事，点击直接对话
            </p>
          </div>
        </AnimatedSection>
        <AnimatedSection>
          <div class="la-matrix">
            <div class="la-matrix__image-wrap">
              <img src="/画板@2x.jpg" alt="AI数字员工矩阵" class="la-matrix__image" @error="onMatrixError" />
            </div>
            <div class="la-matrix__cards">
              <div class="la-matrix__col">
                <div
                  v-for="(card, i) in matrixCards.slice(0, 3)"
                  :key="i"
                  class="la-matrix-card"
                  :class="'la-matrix-card--' + card.bgClass"
                  :style="{ animationDelay: i * 0.1 + 's' }"
                >
                  <div class="la-matrix-card__image-wrap">
                    <img :src="card.image" :alt="card.title" class="la-matrix-card__image" @error="hideImg" />
                  </div>
                  <h4 class="la-matrix-card__title">{{ card.title }}</h4>
                  <p class="la-matrix-card__desc">{{ card.desc }}</p>
                </div>
              </div>
              <div class="la-matrix__col">
                <div
                  v-for="(card, i) in matrixCards.slice(3, 6)"
                  :key="i"
                  class="la-matrix-card"
                  :class="'la-matrix-card--' + card.bgClass"
                  :style="{ animationDelay: (i + 3) * 0.1 + 's' }"
                >
                  <div class="la-matrix-card__image-wrap">
                    <img :src="card.image" :alt="card.title" class="la-matrix-card__image" @error="hideImg" />
                  </div>
                  <h4 class="la-matrix-card__title">{{ card.title }}</h4>
                  <p class="la-matrix-card__desc">{{ card.desc }}</p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>

    <!-- 模块4：平台核心能力 -->
    <section class="la-section la-section--white">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">平台核心能力</h2>
            <p class="la-section__desc">
              不只是通用AI，而是能连接企业系统、理解业务数据、辅助经营决策的企业级AI平台
            </p>
          </div>
        </AnimatedSection>
        <div class="la-grid la-grid--3 la-grid--lg3">
          <AnimatedSection v-for="(item, i) in capabilities" :key="i" :delay="i * 100">
            <div class="la-card la-card--capability" :style="{ backgroundColor: bgColors[i] }">
              <div class="la-card__header">
                <div class="la-card__icon-wrap la-card__icon-wrap--white">
                  <LucideIcon :name="item.iconName" :size="20" :class="'la-icon--' + iconColorClasses[i]" />
                </div>
                <h3 class="la-card__title">{{ item.title }}</h3>
              </div>
              <p class="la-card__desc">{{ item.desc }}</p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    <!-- 模块5：实战场景 -->
    <section class="la-section">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">实战场景</h2>
            <p class="la-section__desc">
              从经营决策到职能效率，AI数字员工渗透企业运转每个环节
            </p>
          </div>
        </AnimatedSection>
        <div class="la-grid la-grid--2">
          <AnimatedSection v-for="(category, catIdx) in scenarios" :key="catIdx" :delay="catIdx * 100">
            <div class="la-scenario" :class="catIdx === 1 ? 'la-scenario--purple' : 'la-scenario--blue'">
              <div class="la-scenario__badge">
                <LucideIcon
                  :name="category.categoryIconName"
                  :size="40"
                  :class="catIdx === 1 ? 'la-icon--purple-fade' : 'la-icon--blue-fade'"
                />
              </div>
              <h3 class="la-scenario__title">{{ category.category }}</h3>
              <div class="la-scenario__list">
                <div v-for="(item, i) in category.items" :key="i" class="la-scenario-item">
                  <div class="la-scenario-item__header">
                    <div
                      class="la-scenario-item__icon-wrap"
                      :class="catIdx === 1 ? 'la-scenario-item__icon-wrap--purple' : 'la-scenario-item__icon-wrap--blue'"
                    >
                      <LucideIcon
                        :name="item.iconName"
                        :size="16"
                        :class="catIdx === 1 ? 'la-icon--purple' : 'la-icon--blue'"
                      />
                    </div>
                    <div class="la-scenario-item__content">
                      <div class="la-scenario-item__top">
                        <h4 class="la-scenario-item__title">{{ item.title }}</h4>
                        <span
                          class="la-badge"
                          :class="catIdx === 1 ? 'la-badge--purple' : 'la-badge--blue'"
                        >{{ item.badge }}</span>
                      </div>
                      <p class="la-scenario-item__desc">{{ item.desc }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    <!-- 模块6：FDE前线部署工程师 -->
    <section class="la-section la-section--white">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">FDE前线部署工程师</h2>
            <p class="la-section__desc">
              不只是交付工具，而是派AI专家驻场，确保系统对接与业务落地
            </p>
          </div>
        </AnimatedSection>
        <div class="la-fde">
          <AnimatedSection>
            <div class="la-fde__steps">
              <div v-for="(step, i) in fdeSteps" :key="i" class="la-fde__step">
                <div class="la-fde__check">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10L8 14L16 6" stroke="#015EDB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <div class="la-fde__step-content">
                  <h4 class="la-fde__step-title">{{ step.title }}</h4>
                  <p class="la-fde__step-desc">{{ step.desc }}</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
          <AnimatedSection :delay="200">
            <div class="la-fde__image-wrap">
              <img src="/配图-leapoAI.jpg" alt="FDE 前线部署工程师" class="la-fde__image" />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    <!-- 模块7：为什么选择LeapoAI -->
    <section class="la-section la-section--gray">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">为什么选择LeapoAI</h2>
            <p class="la-section__desc">
              六大差异化优势，让AI真正融入企业业务血脉
            </p>
          </div>
        </AnimatedSection>
        <div class="la-grid la-grid--3">
          <AnimatedSection v-for="(adv, i) in advantages" :key="i" :delay="i * 100">
            <div class="la-card la-card--advantage">
              <div class="la-card__header">
                <div class="la-card__icon-wrap" :class="'la-card__icon-wrap--' + advantageBgClasses[i]">
                  <LucideIcon :name="advantageIconNames[i]" :size="20" class="la-icon--white" />
                </div>
                <h3 class="la-card__title">{{ adv.title }}</h3>
              </div>
              <p class="la-card__desc">{{ adv.desc }}</p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    <!-- 模块8：灵活的交付方式 -->
    <section class="la-section la-section--white">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">灵活的交付方式</h2>
            <p class="la-section__desc">三种交付模式，按需选择，平滑升级</p>
          </div>
        </AnimatedSection>
        <div class="la-grid la-grid--3">
          <AnimatedSection v-for="(mode, i) in deliveryModes" :key="i" :delay="i * 100">
            <div
              class="la-delivery"
              :class="{ 'la-delivery--recommended': mode.recommended }"
            >
              <div class="la-delivery__inner" :class="{ 'la-delivery__inner--bordered': !mode.recommended }">
                <div v-if="mode.recommended" class="la-delivery__ribbon">推荐</div>
                <div class="la-delivery__header">
                  <LucideIcon :name="mode.iconName" :size="40" :class="'la-icon--' + deliveryIconClasses[i]" />
                  <div>
                    <h3 class="la-delivery__title">{{ mode.title }}</h3>
                    <p class="la-delivery__subtitle">{{ mode.subtitle }}</p>
                  </div>
                </div>
                <ul class="la-delivery__features">
                  <li v-for="(feature, j) in mode.features" :key="j" class="la-delivery__feature">
                    <LucideIcon name="check" :size="16" :class="'la-icon--' + deliveryIconClasses[i]" class="la-delivery__check" />
                    {{ feature }}
                  </li>
                </ul>
                <div class="la-delivery__suitable">
                  <p>{{ mode.suitable }}</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    <!-- 模块9：客户案例 -->
    <section class="la-section la-section--gray">
      <div class="la-container">
        <AnimatedSection>
          <div class="la-section__header">
            <h2 class="la-section__title">客户案例</h2>
            <p class="la-section__desc">
              LeapoAI 助力企业数字化升级，用AI驱动经营决策与效率提升
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection>
          <div class="la-case">
            <div class="la-case__main">
              <div class="la-case__main-left">
                <div class="la-case__label">某生鲜食品企业</div>
                <h3 class="la-case__main-title">AI驱动全渠道智能运营</h3>
                <p class="la-case__main-desc">
                  该企业年营收30亿元，覆盖500+加盟店，业务涉及生鲜采购、仓储物流、门店零售与线上全渠道。随着门店规模扩张，系统数据孤岛、库存周转慢、经营决策滞后等问题日益突出。引入LeapoAI后，快速对接ERP、WMS、CRM等内部系统，AI数字员工全面渗透供应链、财务、运营等核心环节。
                </p>
                <div class="la-case__stats">
                  <div class="la-case__stat">
                    <div class="la-case__stat-num">30亿</div>
                    <div class="la-case__stat-label">年营收规模</div>
                  </div>
                  <div class="la-case__stat">
                    <div class="la-case__stat-num">500+</div>
                    <div class="la-case__stat-label">覆盖加盟店</div>
                  </div>
                  <div class="la-case__stat">
                    <div class="la-case__stat-num">+30%</div>
                    <div class="la-case__stat-label">库存周转提升</div>
                  </div>
                  <div class="la-case__stat">
                    <div class="la-case__stat-num">2周</div>
                    <div class="la-case__stat-label">完成系统对接</div>
                  </div>
                </div>
              </div>
              <div class="la-case__main-right">
                <div class="la-case__image-badge">食品行业·全渠道运营</div>
                <img src="/客户案例leapoAI.jpg" alt="生鲜食品行业案例" class="la-case__image" />
              </div>
            </div>

            <div class="la-case__details">
              <div class="la-case__detail">
                <h4 class="la-case__detail-title">
                  <svg class="la-case__check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  系统集成与数据打通
                </h4>
                <p class="la-case__detail-desc">
                  LeapoAI通过标准API在2周内完成与企业ERP、WMS、CRM、OA等系统的对接，打破数据孤岛，将分散在采购、仓储、销售、财务各环节的数据统一汇聚，AI数字员工可实时调取业务数据进行分析与决策支持。
                </p>
              </div>
              <div class="la-case__detail">
                <h4 class="la-case__detail-title">
                  <svg class="la-case__check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  AI辅助经营决策
                </h4>
                <p class="la-case__detail-desc">
                  库存预测专家基于历史销售数据与市场趋势，精准预测各门店补货需求，智能生成采购建议；财务分析师自动汇总多系统经营数据，一句话生成日报、周报，异常指标实时预警，管理层从"等报表"变为"看实时"。
                </p>
              </div>
              <div class="la-case__detail">
                <h4 class="la-case__detail-title">
                  <svg class="la-case__check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  数字员工覆盖核心岗位
                </h4>
                <p class="la-case__detail-desc">
                  供应商评估专家优化供应商筛选与账期谈判，会议纪要专家自动跟踪总部决议执行，招聘专家与绩效专家支撑500+门店的人员管理，7×24小时在线的AI数字员工让组织运转更高效。
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>

    <!-- 模块10：CTA -->
    <section class="la-cta">
      <div class="la-cta__bg">
        <img src="/底部背景.jpg" alt="" class="la-cta__bg-img" />
      </div>
      <div class="la-container la-cta__inner">
        <AnimatedSection>
          <h2 class="la-cta__title">让AI数字员工成为组织新生产力</h2>
          <p class="la-cta__desc">
            AI数字员工覆盖企业全职能，FDE驻场快速完成系统对接与部署
          </p>
          <div class="la-cta__btn-wrap">
            <a href="javascript:void(0)" class="la-btn la-btn--dark la-btn--arrow" @click="openContact">
              <span>立即预约演示</span>
              <LucideIcon name="arrow-right" :size="16" class="la-btn__icon" />
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  </div>
</template>

<script>
import AnimatedSection from '@/components/AnimatedSection.vue';
import LucideIcon from '@/components/LucideIcon.vue';

const challenges = [
  { title: '系统孤岛，数据难通', desc: 'ERP、CRM、OA、HR等系统各自为政，数据割裂分散，AI无法触达核心业务数据，数字化投入难以形成合力', tags: ['平均3-5套系统并行', '数据打通周期数月'] },
  { title: '经验依赖，人才瓶颈', desc: '核心岗位依赖个人经验，人员流动导致知识流失，新人培养周期长、成本高，优秀经验难以规模化复制', tags: ['培养周期3-6个月', '关键人离职知识断档'] },
  { title: '数据沉睡，决策滞后', desc: '经营数据散落在各系统中，报表靠人工汇总，决策依赖经验直觉，问题发现滞后，错失市场先机', tags: ['报表周期3-7天', '异常响应滞后'] },
];

const matrixCards = [
  { title: '销售与市场类', desc: '市场洞察、销售赋能、品牌传播与新媒体运营', image: '/销售与市场类@2x.png', bgClass: 'blue' },
  { title: '财务与行政类', desc: '记账报表、发票管理、会议纪要与行政事务', image: '/财务与行政类@2x.png', bgClass: 'cyan' },
  { title: '人力与法务类', desc: '招聘绩效、合同审查、制度建设与员工关怀', image: '/人力与法务类@2x.png', bgClass: 'blue' },
  { title: '供应链运营类', desc: '供应商管理、库存预测与采购优化', image: '/供应链运营类@2x.png', bgClass: 'indigo' },
  { title: '产品与研发类', desc: '产品规划、需求分析与项目管理', image: '/产品与研发类@2x.png', bgClass: 'sky' },
  { title: '设计创意类', desc: '创意设计、内容创作与视觉产出', image: '/设计创意类@2x.png', bgClass: 'purple' },
];

const capabilities = [
  { iconName: 'zap', title: '系统快速集成', desc: '标准API快速对接ERP、CRM、OA、HR等企业内部系统，无需替换现有IT投入，AI能力无缝嵌入业务流程，保护已有数字化资产' },
  { iconName: 'brain', title: '全模型接入', desc: '53+大模型供应商接入，包括OpenAI、Claude、DeepSeek、百度、阿里、讯飞、智谱等，一套平台拥有全球AI能力，按场景智能调度最优模型' },
  { iconName: 'database', title: '企业知识库', desc: '制度文档、业务案例、操作手册一键导入，知识可对话、可推理、可溯源，让企业经验变"活资产"，新人培训从7天缩到1天' },
  { iconName: 'bar-chart-3', title: '数据辅助决策', desc: '自然语言查询业务数据，一句话生成经营报表，异常智能预警、趋势自动分析，从"凭经验决策"到"看数据决策"，管理者一眼看清全局' },
  { iconName: 'puzzle', title: '技能生态', desc: '开箱即用数字员工，覆盖销售、财务、人力、法务、供应链等职能；支持自定义技能工厂，能力边界由业务定义，持续扩展无上限' },
  { iconName: 'lock', title: '企业级安全', desc: '全链路加密传输、细粒度权限管控（RBAC）、操作全程审计，支持私有化部署与一体机，数据不出企业，满足金融、政务级合规要求' },
];

const scenarios = [
  {
    category: '经营管理',
    categoryIconName: 'bar-chart-3',
    items: [
      { iconName: 'file', title: '经营分析日报', desc: '多系统数据自动汇总，经营异常智能预警，管理者一眼看清全局', badge: '报表从3天→实时' },
      { iconName: 'truck', title: '供应链智能协同', desc: '供应商评估、库存预测、采购建议，数据驱动供应链优化', badge: '缺货率-40%，周转+35%' },
      { iconName: 'clipboard-list', title: '会议决议追踪', desc: '会议纪要自动生成，决议事项拆解跟踪，执行进度实时可视', badge: '纪要10分钟出稿' },
      { iconName: 'calculator', title: '财务智能分析', desc: '自然语言查账，收支异常预警，经营报表一键生成', badge: '财务人效提升3倍' },
    ],
  },
  {
    category: '职能效率',
    categoryIconName: 'users',
    items: [
      { iconName: 'user-check', title: '智能招聘绩效', desc: 'JD自动生成、简历智能筛选、绩效数据分析，HR效率倍增', badge: '招聘周期缩短50%' },
      { iconName: 'file-check', title: '合同智能审查', desc: '合同风险点自动识别，条款对比分析，审查效率大幅提升', badge: '审查效率+80%' },
      { iconName: 'pen-tool', title: '营销内容工厂', desc: '公众号、小红书、抖音内容一键生成，品牌策略智能输出', badge: '内容产出10倍' },
      { iconName: 'headphones', title: '7×24智能客服', desc: '多轮对话、工单自动分发、知识库精准应答，客户问题秒级响应', badge: '解决率92%' },
    ],
  },
];

const fdeSteps = [
  { title: '驻场理解', desc: '深入企业业务一线，梳理系统现状与流程痛点，制定AI升级路径' },
  { title: '快速定制', desc: '对接企业内部系统，定制数字员工，1-2周完成部署上线' },
  { title: '经验复用', desc: '业务效果跟踪，技能持续迭代，成功经验跨部门复制推广' },
];

const advantages = [
  { title: '易集成', desc: '标准API快速对接ERP/CRM/OA等企业系统，保护现有IT投资，AI无缝嵌入' },
  { title: '全模态', desc: '文/图/音/视全能力覆盖，一套协议接入53+供应商' },
  { title: '高可用', desc: '多级负载均衡+故障自动转移，最多5次故障转移' },
  { title: '强管控', desc: 'Token计量+RBAC+全链路审计，满足企业级合规要求' },
  { title: '灵活部署', desc: 'SaaS/一体机/私有化，按需选择，数据主权在我' },
  { title: 'FDE落地', desc: '驻场专家+飞轮机制，交付可量化业务结果' },
];

const deliveryModes = [
  { iconName: 'cloud', title: 'SaaS订阅版', subtitle: 'STARTER', features: ['快速开通，无需部署', '按席位/Token计费', '标准功能全包含', '持续迭代升级'], suitable: '适合：中小企业/团队', recommended: false },
  { iconName: 'server', title: '一体机私有化', subtitle: 'PROFESSIONAL', features: ['本地算力+预置模型', '即开即用，数据不出门', '完整企业级管控', '满足数据主权要求'], suitable: '适合：中大型企业', recommended: true },
  { iconName: 'users', title: 'FDE定制服务', subtitle: 'ENTERPRISE', features: ['驻场专家+数字员工定制', '业务结果承诺', '飞轮机制持续优化', '行业标杆共建'], suitable: '适合：集团企业/行业标杆', recommended: false },
];

export default {
  name: 'LeapoAiContent',
  components: { AnimatedSection, LucideIcon },
  data() {
    return {
      challenges,
      matrixCards,
      capabilities,
      scenarios,
      fdeSteps,
      advantages,
      deliveryModes,
      challengeIconNames: ['database', 'brain', 'bar-chart-3'],
      advantageIconNames: ['zap', 'brain', 'database', 'lock', 'puzzle', 'bar-chart-3'],
      bgColors: ['#EFF6FF', '#F4F4FF', '#EFF6FF', '#F4F4FF', '#EFF6FF', '#F4FBFD'],
      iconColorClasses: ['blue', 'purple', 'blue', 'purple', 'blue', 'teal'],
      advantageBgClasses: ['blue', 'cyan', 'indigo', 'sky', 'teal', 'violet'],
      deliveryIconClasses: ['teal', 'brand', 'orange'],
    };
  },
  mounted() {
    document.title = 'LeapoAI | 汇通数科';
  },
  methods: {
    openContact() {
      this.$root.$emit('openContactUs', 'LeapoAI');
    },
    onMatrixError(e) {
      e.target.style.display = 'none';
      e.target.parentElement.classList.add('la-img-placeholder');
    },
    hideImg(e) {
      e.target.style.display = 'none';
    },
  },
};
</script>

<style lang="less" scoped>
// ---- Root: 创建独立层叠上下文，确保内部 z-index 不会逃逸盖过 Header 弹窗 ----
.leapo-ai {
  position: relative;
  z-index: 1;
}

// ---- Design tokens ----
@la-blue: #015EDB;
@la-blue-light: #1B67DD;
@la-ink: #0a0a0a;
@la-gray-50: #f9fafb;
@la-gray-100: #f3f4f6;
@la-gray-200: #e5e7eb;
@la-gray-400: #9ca3af;
@la-gray-500: #6b7280;
@la-gray-600: #4b5563;
@la-purple: #7C3AED;
@la-teal: #14b8a6;
@la-orange: #f97316;
@la-cyan: #06b6d4;
@la-indigo: #6366f1;
@la-sky: #0ea5e9;
@la-violet: #8b5cf6;
@la-screen-sm: 640px;
@la-screen-md: 768px;
@la-screen-lg: 1024px;

.la-container {
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 24px;
  padding-right: 24px;

  @media (min-width: @la-screen-lg) {
    padding-left: 32px;
    padding-right: 32px;
  }
}

.la-img-placeholder {
  background-color: @la-gray-100;
  display: flex;
  align-items: center;
  justify-content: center;
}

// ---- Base ----
.la-icon--white { color: #ffffff; }
.la-icon--blue { color: @la-blue; }
.la-icon--purple { color: @la-purple; }
.la-icon--teal { color: @la-teal; }
.la-icon--brand { color: @la-blue; }
.la-icon--orange { color: @la-orange; }
.la-icon--blue-fade { color: fade(@la-blue, 30%); }
.la-icon--purple-fade { color: fade(@la-purple, 30%); }

// ---- Buttons ----
.la-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  width: 180px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border: none;

  span {
    transition: transform 0.3s ease-out;
  }

  &:hover span {
    transform: translateX(-4px);
  }

  &:hover .la-btn__icon {
    opacity: 1;
    transform: translateX(0);
  }

  &__icon {
    position: absolute;
    right: 24px;
    opacity: 0;
    transform: translateX(8px);
    transition: all 0.3s ease-out;
  }

  &--primary {
    background-color: @la-blue-light;
    color: #ffffff;

    &:hover {
      background-color: darken(@la-blue-light, 8%);
    }
  }

  &--dark {
    background-color: @la-ink;
    color: #ffffff;

    &:hover {
      background-color: lighten(@la-ink, 15%);
    }
  }
}

// ---- Section shared ----
.la-section {
  padding-top: 80px;
  padding-bottom: 80px;
  background-color: #ffffff;

  @media (min-width: @la-screen-md) {
    padding-top: 112px;
    padding-bottom: 112px;
  }

  &--gray {
    background-color: @la-gray-50;
  }

  &--white {
    background-color: #ffffff;
  }

  &__header {
    text-align: center;
    margin-bottom: 64px;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    color: @la-ink;
    margin: 0 0 16px;
    line-height: 1.2;

    @media (min-width: @la-screen-md) {
      font-size: 36px;
    }
  }

  &__desc {
    font-size: 13px;
    color: @la-gray-500;
    text-align: center;
    margin: 0;
    line-height: 1.6;

    @media (min-width: @la-screen-md) {
      font-size: 16px;
    }
  }
}

// ---- Grid ----
.la-grid {
  display: grid;
  gap: 24px;
  text-align: left;

  &--2 {
    grid-template-columns: 1fr;

    @media (min-width: @la-screen-lg) {
      grid-template-columns: repeat(2, 1fr);
      gap: 32px;
    }
  }

  &--3 {
    grid-template-columns: 1fr;

    @media (min-width: @la-screen-md) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: @la-screen-lg) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &--lg3 {
    @media (min-width: @la-screen-lg) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

// ---- Banner (Module 1) ----
.la-banner {
  position: relative;
  overflow: hidden;
  background-color: #ffffff;
  height: auto;

  @media (min-width: @la-screen-md) {
    height: 600px;
  }

  &__bg {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  &__bg-img {
    width: 1690px;
    height: 600px;
    object-fit: cover;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, #F8FAFC, transparent);
  }

  &__inner {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    padding-top: 160px;
    padding-bottom: 80px;

    @media (min-width: @la-screen-md) {
      padding-top: 160px;
      padding-bottom: 0;
    }
  }

  &__grid {
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: center;
    width: 100%;

    // 文本列左对齐
    > .animated-section:first-child {
      width: 100%;
      text-align: left;
    }

    @media (min-width: @la-screen-md) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 48px;
    }
  }

  &__title {
    font-size: 28px;
    font-weight: 700;
    color: @la-ink;
    line-height: 1.1;
    margin: 0 0 16px;

    @media (min-width: @la-screen-md) {
      font-size: 44px;
    }
  }

  &__subtitle {
    font-size: 24px;
    font-weight: 700;
    color: @la-ink;
    line-height: 1.1;
    margin: 0 0 24px;

    @media (min-width: @la-screen-md) {
      font-size: 52px;
    }
  }

  &__desc {
    font-size: 13px;
    color: @la-gray-500;
    line-height: 1.7;
    margin: 0 0 32px;
    max-width: 560px;

    @media (min-width: @la-screen-md) {
      font-size: 15px;
    }
  }

  &__image-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__image {
    width: 100%;
    height: auto;
    max-height: none;
  }
}

// ---- Cards ----
.la-card {
  background-color: #ffffff;
  border-radius: 16px;
  padding: 32px;
  height: 100%;
  display: flex;
  flex-direction: column;

  &--challenge {
    background-color: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  &--capability {
    border-radius: 16px;
    padding: 24px;
  }

  &--advantage {
    border: 1px solid #eeeeee;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  &__icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--blue {
      background-color: #3b82f6;
    }

    &--blue-light {
      background-color: fade(@la-blue-light, 10%);
    }

    &--white {
      background-color: #ffffff;
    }

    // advantage bg colors
    &--cyan { background-color: @la-cyan; }
    &--indigo { background-color: @la-indigo; }
    &--sky { background-color: @la-sky; }
    &--teal { background-color: @la-teal; }
    &--violet { background-color: @la-violet; }
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: @la-ink;
    margin: 0;

    @media (min-width: @la-screen-md) {
      font-size: 18px;
    }
  }

  &__desc {
    font-size: 14px;
    color: @la-gray-500;
    line-height: 1.7;
    margin: 0 0 24px;
  }
}

.la-card--challenge .la-card__desc {
  margin-bottom: 24px;
}

.la-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
}

.la-tag {
  padding: 4px 12px;
  background-color: @la-gray-100;
  color: @la-gray-500;
  font-size: 12px;
  border-radius: 9999px;
}

// ---- Matrix (Module 3) ----
.la-matrix {
  position: relative;

  @media (min-width: @la-screen-md) {
    position: relative;
  }

  &__image-wrap {
    aspect-ratio: 16 / 9;
    border-radius: 24px;
    max-width: 896px;
    margin: 0 auto;
    overflow: hidden;
    background-color: @la-gray-100;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__cards {
    @media (min-width: @la-screen-md) {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      padding: 0 16px;
    }

    margin-top: 32px;

    @media (min-width: @la-screen-md) {
      margin-top: 0;
    }

    width: 100%;
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 24px;
    padding-right: 24px;
    display: flex;
    justify-content: space-between;

    @media (min-width: @la-screen-lg) {
      padding-left: 32px;
      padding-right: 32px;
    }
  }

  &__col {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 240px;
  }
}

.la-matrix-card {
  background: linear-gradient(to right, #ffffff, #ffffff);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &--blue { background: linear-gradient(to right, #EFF6FF, #ffffff); }
  &--cyan { background: linear-gradient(to right, #ECFEFF, #ffffff); }
  &--indigo { background: linear-gradient(to right, #EEF2FF, #ffffff); }
  &--sky { background: linear-gradient(to right, #F0F9FF, #ffffff); }
  &--purple { background: linear-gradient(to right, #F5F3FF, #ffffff); }

  &__image-wrap {
    margin-bottom: 12px;
    position: relative;
    z-index: 10;
    width: 100%;
    height: 50px;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__title {
    margin: 0 0 8px;
    position: relative;
    z-index: 10;
    text-align: left;
    font-size: 18px;
    font-weight: 600;
    color: @la-ink;
  }

  &__desc {
    margin: 0;
    position: relative;
    z-index: 10;
    font-size: 14px;
    color: @la-gray-500;
    line-height: 1.7;
  }
}

// ---- Scenarios (Module 5) ----
.la-scenario {
  border-radius: 24px;
  padding: 32px;
  height: 100%;
  position: relative;
  overflow: hidden;

  @media (min-width: @la-screen-md) {
    padding: 40px;
  }

  &--blue {
    background-color: #EFF6FF;
  }

  &--purple {
    background-color: #F5F3FF;
  }

  &__badge {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(to bottom right, fade(@la-blue, 10%), fade(@la-indigo, 10%));

    .la-scenario--purple & {
      background: linear-gradient(to bottom right, fade(@la-purple, 10%), fade(@la-violet, 10%));
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 700;
    color: @la-ink;
    margin: 0 0 24px;
    position: relative;
  }

  &__list {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.la-scenario-item {
  background-color: #ffffff;
  border-radius: 16px;
  padding: 24px;

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--blue {
      background-color: fade(@la-blue-light, 10%);
    }

    &--purple {
      background-color: fade(@la-purple, 10%);
    }
  }

  &__content {
    flex: 1;
  }

  &__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: @la-ink;
    margin: 0;

    @media (min-width: @la-screen-md) {
      font-size: 18px;
    }
  }

  &__desc {
    font-size: 14px;
    color: @la-gray-500;
    line-height: 1.7;
    margin: 0;
  }
}

.la-badge {
  padding: 2px 8px;
  font-size: 10px;
  border-radius: 9999px;
  font-weight: 500;
  white-space: nowrap;
  margin-left: 8px;
  flex-shrink: 0;

  @media (min-width: @la-screen-md) {
    padding: 4px 12px;
    font-size: 12px;
    margin-left: 16px;
  }

  &--blue {
    background-color: fade(@la-blue-light, 10%);
    color: @la-blue;
  }

  &--purple {
    background-color: fade(@la-purple, 10%);
    color: @la-purple;
  }
}

// ---- FDE (Module 6) ----
.la-fde {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;

  @media (min-width: @la-screen-lg) {
    grid-template-columns: repeat(2, 1fr);
    gap: 48px;
  }

  &__steps {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  &__step {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  &__step-content {
    text-align: left;
  }

  &__check {
    flex-shrink: 0;
    margin-top: 4px;
  }

  &__step-title {
    font-size: 17px;
    font-weight: 700;
    color: @la-ink;
    margin: 0 0 8px;

    @media (min-width: @la-screen-md) {
      font-size: 22px;
    }
  }

  &__step-desc {
    font-size: 13px;
    color: @la-gray-500;
    line-height: 1.7;
    margin: 0;

    @media (min-width: @la-screen-md) {
      font-size: 16px;
    }
  }

  &__image-wrap {
    aspect-ratio: 4 / 3;
    border-radius: 24px;
    overflow: hidden;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// ---- Delivery (Module 8) ----
.la-delivery {
  position: relative;
  border-radius: 16px;
  height: 100%;

  &--recommended {
    padding: 2px;
    background: linear-gradient(to bottom, #93A8EF, #F8F9FC);
  }

  &__inner {
    position: relative;
    border-radius: 16px;
    height: 100%;
    background-color: #ffffff;
    padding: 32px;

    &--bordered {
      border: 1px solid @la-gray-200;
    }
  }

  &__ribbon {
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 24px;
    color: #ffffff;
    font-size: 14px;
    border-radius: 9999px;
    font-weight: 500;
    z-index: 10;
    background: linear-gradient(to right, #5C57EB, #6092FF);
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: @la-ink;
    margin: 0;

    @media (min-width: @la-screen-md) {
      font-size: 20px;
    }
  }

  &__subtitle {
    font-size: 11px;
    color: @la-gray-400;
    letter-spacing: 0.05em;
    margin: 4px 0 0;

    @media (min-width: @la-screen-md) {
      font-size: 12px;
    }
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 0 0 32px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__feature {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 13px;
    color: @la-ink;

    @media (min-width: @la-screen-md) {
      font-size: 15px;
    }
  }

  &__check {
    margin-top: 2px;
    flex-shrink: 0;
  }

  &__suitable {
    padding-top: 16px;
    border-top: 1px solid @la-gray-200;
    text-align: left;

    p {
      font-size: 13px;
      color: @la-ink;
      font-weight: 500;
      margin: 0;

      @media (min-width: @la-screen-md) {
        font-size: 16px;
      }
    }
  }
}

// ---- Case (Module 9) ----
.la-case {
  &__main {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;

    @media (min-width: @la-screen-lg) {
      grid-template-columns: 60fr 40fr;
      gap: 48px;
    }
  }

  &__main-left {
    text-align: left;
  }

  &__label {
    font-size: 13px;
    color: @la-gray-500;
    margin-bottom: 8px;

    @media (min-width: @la-screen-md) {
      font-size: 18px;
    }
  }

  &__main-title {
    font-size: 17px;
    font-weight: 700;
    color: @la-ink;
    line-height: 1.2;
    margin: 0 0 16px;

    @media (min-width: @la-screen-md) {
      font-size: 28px;
    }

    @media (min-width: @la-screen-lg) {
      font-size: 32px;
    }
  }

  &__main-desc {
    font-size: 13px;
    color: @la-gray-500;
    line-height: 1.7;
    margin: 0 0 48px;

    @media (min-width: @la-screen-md) {
      font-size: 16px;
      margin-bottom: 48px;
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;

    @media (min-width: @la-screen-md) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  &__stat-num {
    font-size: 24px;
    font-weight: 700;
    color: @la-blue;
    margin-bottom: 8px;

    @media (min-width: @la-screen-md) {
      font-size: 32px;
    }
  }

  &__stat-label {
    font-size: 14px;
    color: @la-gray-500;
  }

  &__main-right {
    position: relative;
    background: linear-gradient(to bottom right, #eff6ff, #e0e7ff);
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;

    @media (min-width: @la-screen-lg) {
      min-height: 400px;
    }
  }

  &__image-badge {
    position: absolute;
    top: 16px;
    left: 16px;
    padding: 6px 12px;
    background-color: rgba(10, 10, 10, 0.5);
    color: #ffffff;
    font-size: 11px;
    border-radius: 9999px;
    font-weight: 500;
    z-index: 10;

    @media (min-width: @la-screen-md) {
      top: 24px;
      left: 24px;
      padding: 8px 16px;
      font-size: 14px;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;
  }

  &__details {
    display: grid;
    grid-template-columns: 1fr;
    gap: 48px;
    padding-top: 64px;
    text-align: left;

    @media (min-width: @la-screen-md) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__detail-title {
    font-size: 17px;
    font-weight: 700;
    color: @la-ink;
    margin: 0 0 16px;
    display: flex;
    align-items: center;
    gap: 12px;

    @media (min-width: @la-screen-md) {
      font-size: 20px;
    }
  }

  &__check-icon {
    color: @la-blue;
    flex-shrink: 0;
  }

  &__detail-desc {
    font-size: 13px;
    color: @la-gray-500;
    line-height: 1.7;
    margin: 0;

    @media (min-width: @la-screen-md) {
      font-size: 15px;
    }
  }
}

// ---- CTA (Module 10) ----
.la-cta {
  height: 400px;
  background-color: #F0EEF6;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &__bg {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  &__bg-img {
    width: 1600px;
    height: 400px;
    object-fit: cover;
    object-position: center;
  }

  &__inner {
    position: relative;
    z-index: 10;
    text-align: center;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    color: @la-ink;
    margin: 0 0 16px;

    @media (min-width: @la-screen-md) {
      font-size: 36px;
    }
  }

  &__desc {
    font-size: 13px;
    color: @la-gray-500;
    text-align: center;
    margin: 0;

    @media (min-width: @la-screen-md) {
      font-size: 16px;
    }
  }

  &__btn-wrap {
    margin-top: 32px;
  }
}
</style>
